// @flow
import React, {Fragment} from "react";
import {Alert, Button, ButtonGroup, ButtonToolbar, Card, CardBody, CardHeader, CardSubtitle, CardTitle, Progress} from "reactstrap";
import _ from "lodash";
import BlobCardImg from "./BlobCardImg";
import config from "../../../config/config";
import {Link, withRouter} from "react-router-dom";
import type {History, Location} from "../../../types/ReactRouterTypes";
import * as queryString from "query-string";
import DeleteEBadgePhotoButton from "../../../containers/ebadge/photo/DeleteEBadgePhotoButton";
import type {PhotoEBadgeSearchCriteria} from "./PhotoEBadgeSearchForm";
import SelectablePaginator from "../../SelectablePaginator";
import Idle from "react-idle";
import PhotoCheckbox from "./PhotoCheckbox";
import BulkDeletePhotoButton from "./BulkDeletePhotoButton";
import BulkDeleteModal from "../../../containers/ebadge/photo/BulkDeleteModal";
import {parseSearch} from "./PhotoEBadgeQueryPage";
import './photo-ebadge-query-result.css';
import ValidateEBadgePhotoModal from "../../../containers/ebadge/photo/ValidateEBadgePhotoModal";
import ValidateEBadgePhotoButton from "../../../containers/ebadge/photo/ValidateEBadgePhotoButton";
import InvalidateEBadgePhotoButton from "../../../containers/ebadge/photo/InvalidateEBadgePhotoButton";
import InvalidateEBadgePhotoModal from "../../../containers/ebadge/photo/InvalidateEBadgePhotoModal";
import "../ebadge-result.css";

export type PhotoEBadge = {
    registrationCode: string,
    verifyStatus: "Y" | "N"
}

export type PhotoEBadgeQueryResultType = {
    list: Array<PhotoEBadge>,
    totalResult: number
}

export type PhotoEBadgeQueryResultStateProps = {
    totalCount: number,
    currentPage: number,
    eBadges: Array<PhotoEBadge>,
    isLoading: boolean,
    error?: any,
    idleTimeout: number,
    enableDeleteButtons: boolean,
    enableVerifyButtons: boolean
}

export type PhotoEBadgeQueryResultDispatchProps = {
    onPageClick: (page: number, criteria: PhotoEBadgeSearchCriteria) => any,
    resetResult: () => any,
    togglePhoto: (registrationCode: string) => any
}

type PhotoEBadgeQueryResultProps = PhotoEBadgeQueryResultStateProps & PhotoEBadgeQueryResultDispatchProps & {
    location: Location,
    history: History
};

function pageItemLocation(query: any, location: Location, pageItem: number): Location {

    return {
        ...location,
        search: "?" + queryString.stringify({...query, page: 1, pageItem})
    };
}

function PhotoEBadgeQueryResult(props: PhotoEBadgeQueryResultProps) {

    if (props.isLoading) {

        return <Progress animated color="danger" value="100"/>
    } else if (props.currentPage > 0 && props.totalCount === 0) {

        return <Alert color={"warning"}>No Record Found!</Alert>
    } else if (props.currentPage > 0 && props.totalCount > 0) {

        const location: Location = props.location;
        const search: string = location.search;
        let query = queryString.parse(search);

        const criteria: PhotoEBadgeSearchCriteria = parseSearch(location.search);

        return (
            <Fragment>
                <div className={"clearfix mb-1"}>
                    <div className={'float-left'}>
                        Total Result: {props.totalCount}
                    </div>
                    <div className={'float-right'}>
                        <ButtonToolbar>
                            {
                                props.enableDeleteButtons &&
                                <ButtonGroup className={"ml-1"}>
                                    <BulkDeletePhotoButton/>
                                </ButtonGroup>
                            }
                            <ButtonGroup className={"ml-1"}>
                                <Button tag={Link} to={pageItemLocation(query, location, 20)}
                                        color={!criteria.pageItem || +criteria.pageItem === 20 ? 'primary' : undefined} onClick={() => {
                                    criteria.pageItem = 20;
                                    props.onPageClick(1, criteria);
                                }}>20</Button>
                                <Button tag={Link} to={pageItemLocation(query, location, 40)}
                                        color={+criteria.pageItem === 40 ? 'primary' : undefined} onClick={() => {
                                    criteria.pageItem = 40;
                                    props.onPageClick(1, criteria);
                                }}>40</Button>
                                <Button tag={Link} to={pageItemLocation(query, location, 60)}
                                        color={+criteria.pageItem === 60 ? 'primary' : undefined} onClick={() => {
                                    criteria.pageItem = 60;
                                    props.onPageClick(1, criteria);
                                }}>60</Button>
                            </ButtonGroup>
                        </ButtonToolbar>
                    </div>
                </div>
                <div id={'photo-ebadge-query-result'} className={"row"}>
                    {
                        _.map(props.eBadges, (eBadge: PhotoEBadge, i: number) => (
                            <div key={i} className={"col-xl-2 col-lg-3 col-md-6 col-sm-12 mb-5"}>
                                <Card>
                                    {
                                        props.enableDeleteButtons &&
                                        <CardHeader>
                                            <PhotoCheckbox enabled={eBadge.verifyStatus !== "Y"} registrationCode={eBadge.registrationCode}/>
                                        </CardHeader>
                                    }
                                    <BlobCardImg top
                                                 onClick={() => {
                                                     if (eBadge.verifyStatus !== "Y") {
                                                        props.togglePhoto(eBadge.registrationCode)
                                                     }
                                                 }}
                                                 className={eBadge.verifyStatus !== "Y" ? "clickable" : ""}
                                                 src={`${config.apiGatewayProcotol}://${config.apiGatewayHostname}/ebadge-admin/ebadge-photo/v1/photos/${eBadge.registrationCode}`}
                                                 alt={eBadge.registrationCode}/>
                                    <CardBody>
                                        <CardTitle>{eBadge.verifyStatus === "Y" ? "Validated" : "Invalidated"}</CardTitle>
                                        <CardSubtitle><Link to={`/query/ebadges?cQw08la1wX=${new Date().getTime()}&page=1&pageItem=20&registrationCode=${eBadge.registrationCode}`}>{eBadge.registrationCode}</Link></CardSubtitle>
                                        <div className={"btn-container"}>
                                        {
                                            eBadge.verifyStatus !== "Y" && props.enableDeleteButtons &&
                                            <DeleteEBadgePhotoButton className={"btn"} color={"danger"} label={"Delete"} registrationCode={eBadge.registrationCode}
                                                                     unlockCount={0}/>
                                        }
                                        {
                                            eBadge.verifyStatus !== "Y" && props.enableVerifyButtons &&
                                            <ValidateEBadgePhotoButton className={"btn light-green"} color={"success"} label={"Validate"} registrationCode={eBadge.registrationCode}
                                                                     unlockCount={0}/>
                                        }
                                        {
                                            eBadge.verifyStatus === "Y" && props.enableVerifyButtons &&
                                            <InvalidateEBadgePhotoButton className={"btn light-red"} color={"danger"} label={"Invalidate"} registrationCode={eBadge.registrationCode}
                                                                     unlockCount={0}/>
                                        }
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>)
                        )
                    }
                </div>
                <div className={"row"}>
                    <SelectablePaginator currentPage={+props.currentPage} pageItem={+query.pageItem || 20} totalCount={props.totalCount}
                                         generateLink={(page: number): Location => {

                                             query.page = page;
                                             return {
                                                 ...location,
                                                 search: "?" + queryString.stringify(query)
                                             };
                                         }} onClickPage={(page: number) => {
                        props.onPageClick(page, criteria);
                    }}/>
                </div>
                <BulkDeleteModal />
                <ValidateEBadgePhotoModal />
                <InvalidateEBadgePhotoModal />
            </Fragment>
        );
    } else if (typeof props.error !== 'undefined') {

        return <Alert color={"danger"}><span dangerouslySetInnerHTML={{__html:props.error.message }}/></Alert>
    } else if (props.currentPage === 0) {
        return null;
    } else {
        return <Alert color={"danger"}>Unexpected Error</Alert>
    }
}

function IdleResult(props: PhotoEBadgeQueryResultProps) {
    return (
        <Idle onChange={(idle) => {
            if (idle.idle) {
                props.resetResult();
            }
        }} timeout={props.idleTimeout} render={({idle}) =>
            <Fragment>
                {idle
                    ? null
                    : <PhotoEBadgeQueryResult {...props}/>
                }
            </Fragment>
        }/>
    );
}

export default withRouter(IdleResult);


// WEBPACK FOOTER //
// ./src/components/ebadge/photo/PhotoEBadgeQueryResult.js